<template>
  <div class="page has-compressed-header">
    <BotPreviewModal v-if="showBotPreviewModal" @cancel="showBotPreviewModal = false" :flowToPreview="flowId" />
    <n-loading-spinner v-if="!flowLoaded" />
    <section v-if="flowLoaded" class="section primary-header">
      <nav class="level container is-fluid">
        <div class="level-left">
          <router-link :to="{ name: 'bots-flows' }">
            <div class="back-arrow">
              <BackArrow class="icon-1-5x"></BackArrow>
            </div>
          </router-link>

          <h4 class="title is-4 flow-title">
            <EditableText :value="flowName" @text-updated="setFlowName" minWidth="100px" maxWidth="600px">
            </EditableText>

            <div class="flow-status is-flex">
              <n-badge v-if="currentFlowStatus === 'live'" variant="success">Live</n-badge>
              <n-badge v-if="currentFlowStatus === 'paused'" variant="secondary">Paused</n-badge>
              <n-badge v-if="currentFlowStatus === 'draft'" variant="default">Draft</n-badge>
            </div>
          </h4>
        </div>

        <div class="level-right">
          <p class="level-item">
            <n-tooltip position="bottom" :text="saveFlowErrorMessage">
              <n-button
                variant="secondary"
                :disabled="!isSaveButtonEnabled"
                @click="saveButtonClicked()"
                id="save-flow-button"
              >
                <div class="button-content"><SaveIcon /> Save Changes</div>
              </n-button>
            </n-tooltip>
          </p>

          <p class="level-item">
            <n-tooltip position="bottom" :text="previewFlowErrorMessage">
              <n-button
                variant="secondary--outline"
                :disabled="!isPreviewButtonEnabled"
                @click="previewButtonClicked()"
                id="preview-bot-button"
              >
                <div class="button-content"><PlayIcon /> Preview</div>
              </n-button>
            </n-tooltip>
          </p>

          <AdvancedTooltip v-if="!isBotStudioEnabled() && isMakeLiveButtonVisible()" class="level-item">
            <template v-slot:element>
              <p>
                <n-button
                  variant="success"
                  :disabled="!canMakeLive"
                  @click="makeLiveButtonClicked()"
                  id="make-live-button"
                >
                  <div class="button-content"><PlayIcon /> Make Live</div>
                </n-button>
              </p>
            </template>
            <template v-slot:content>
              <div><strong>Upgrade to unlock this feature</strong></div>
              <div>Get flows, FAQs and more</div>
            </template>
            <template v-slot:footer>
              <button class="button is-primary is-outlined is-fullwidth" @click="upgradeButtonClicked()">
                Upgrade
              </button>
            </template>
          </AdvancedTooltip>

          <p v-if="isMakeLiveButtonVisible() && isBotStudioEnabled()" class="level-item">
            <n-tooltip position="bottom" :text="makeLiveErrorMessage">
              <n-button
                variant="success"
                :disabled="!canMakeLive"
                @click="makeLiveButtonClicked()"
                id="make-live-button"
              >
                <div class="button-content"><PlayIcon /> Make Live</div>
              </n-button>
            </n-tooltip>
          </p>

          <p class="level-item">
            <DropDownMenu :goLeft="true" :useColourOnActive="true">
              <a @click="pauseFlowClicked()" v-if="isPauseOptionVisible()" class="dropdown-item" id="pause-flow-option">
                <PauseIcon />
                Pause Flow
              </a>
              <a @click="deleteFlowClicked" class="dropdown-item" id="delete-flow-option">
                <DeleteIcon />
                Delete
              </a>
            </DropDownMenu>
          </p>
        </div>
      </nav>
    </section>

    <section v-if="flowLoaded" class="section content-container" :class="{ 'is-disabled': isSaveFlowInProgress }">
      <router-view></router-view>
    </section>

    <ConfirmationModal
      id="delete-flow-modal"
      v-if="showDeleteModal"
      confirmationTitle="Delete flow"
      :confirmationMessage="getDeleteConfirmationMessage()"
      @confirm="deleteFlow"
      @cancel="showDeleteModal = false"
    >
    </ConfirmationModal>

    <ConfirmationModal
      id="leave-modal"
      ref="leaveModal"
      v-if="showLeaveModal"
      confirmationTitle="Discard Changes"
      :confirmationMessage="getLeaveConfirmationMessage()"
      @confirm="handleConfirmLeave"
      @cancel="handleCancelLeave"
    >
    </ConfirmationModal>

    <ConfirmationModal
      id="pause-flow-modal"
      v-if="showPauseFlowModal"
      confirmationTitle="Pause Flow"
      :confirmationMessage="getPauseConfirmationMessage()"
      @confirm="pauseFlow"
      @cancel="showPauseFlowModal = false"
    >
    </ConfirmationModal>

    <ConfirmationModal
      id="make-live-modal"
      v-if="showMakeLiveModal"
      confirmationTitle="Make Live"
      :confirmationMessage="getMakeLiveConfirmationMessage()"
      @confirm="makeFlowLive"
      @cancel="showMakeLiveModal = false"
    >
    </ConfirmationModal>

    <ConfirmationModal
      id="no-trigger-modal"
      ref="noTriggerModal"
      v-if="showNoTriggerModal"
      confirmationTitle="No triggers defined"
      :confirmationMessage="getNoTriggerSelectedConfirmationMessage()"
      confirm-button-label="Save"
      v-on:cancel="showNoTriggerModal = false"
      v-on:confirm="saveChanges()"
    >
    </ConfirmationModal>

    <UpgradeModal v-if="showUpgradeModal" @close="showUpgradeModal = false"></UpgradeModal>
  </div>
</template>

<script lang="ts" setup>
import { LOAD_FLOW, LOAD_FLOWS, SAVE_FLOW, UPDATE_STATUS } from '@/store/bot-designer/actionTypes';
import { MutationPayload, useStore } from 'vuex';
import { ADD_PATH, UPDATE_PATH, REMOVE_PATH } from '@/store/bot-designer/paths/mutationTypes';
import { ADD_STATE, REMOVE_STATE, UPDATE_STATE } from '@/store/bot-designer/states/mutationTypes';
import Toast from '@/shared/services/toast';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import EditableText from '@/shared/components/EditableText.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import BackArrow from 'vue-material-design-icons/ArrowLeft.vue';
import {
  MAKE_DIRTY,
  RESET_CHANGE_TRACKER,
  SET_FLOW_NAME,
  SET_IS_SAVE_FLOW_IN_PROGRESS,
} from '@/store/bot-designer/mutationTypes';
import {
  SET_TRIGGER,
  UPDATE_SCHEDULE_CONDITION,
  UPDATE_TRIGGER_CONDITIONS,
  UPDATE_AUDIENCE_CONDITIONS,
  UPDATE_TARGET_CONTACT_TYPES,
} from '@/store/bot-designer/triggers/mutationTypes';
import { ChangeType } from '@/features/bots/models/designer';
import { useRouter, onBeforeRouteUpdate, onBeforeRouteLeave } from 'vue-router';
import { flowsApi } from '@/features/bots/services/flows';
import { ParentViewModel } from '@/features/bots/models/parentViewModel';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import appInsights from '@/shared/services/appInsights';
import { BOT_STUDIO_ENABLED } from '@/store/app/featureTypes';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import BotPreviewModal from '@/features/bots/components/botTestTool/BotPreviewModal.vue';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import PauseIcon from 'vue-material-design-icons/Pause.vue';
import PlayIcon from 'vue-material-design-icons/Play.vue';
import SaveIcon from 'vue-material-design-icons/ContentSave.vue';
import AdvancedTooltip from '@/shared/components/AdvancedTooltip.vue';
import UpgradeModal from '@/features/bots/components/UpgradeModal.vue';
import { LOAD_INTENTS } from '@/store/intents/actionTypes';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  flowId: {
    type: String,
    required: true,
  },
});

const router = useRouter();

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const flowName = computed(() => store.getters.flowName);
const hasMadeChanges = computed(() => store.getters.hasMadeChanges);
const isSaveFlowInProgress = computed(() => store.getters.isSaveFlowInProgress);
const isFlowInvalid = computed(() => store.getters.isFlowInvalid);
const isCurrentFlowTriggerable = computed(() => store.getters.isCurrentFlowTriggerable);
const currentFlowStatus = computed<FlowStatus>(() => store.getters.currentFlowStatus);
const isTransitioningToAnInactiveFlow = computed<boolean>(() => store.getters.isTransitioningToAnInactiveFlow);
const parentFlows = computed<ParentViewModel[]>(() => store.getters.parentFlows);
const parentAnswers = computed<ParentViewModel[]>(() => store.getters.parentAnswers);
const isWorkspaceFeatureEnabled = (feature: string) => computed(() => store.getters.isWorkspaceFeatureEnabled(feature));

const loadFlow = (flowId: string) => store.dispatch(LOAD_FLOW, flowId);
const saveFlow = () => store.dispatch(SAVE_FLOW);
const loadFlows = (forceLoad: boolean = false) => store.dispatch(LOAD_FLOWS, forceLoad);
const loadIntents = (forceLoad: boolean = false) => store.dispatch(LOAD_INTENTS, forceLoad);
const updateFlowStatus = (status: FlowStatus): Promise<void> => store.dispatch(UPDATE_STATUS, status);

const setFlowName = (name: string) => store.commit(SET_FLOW_NAME, name);
const makeDirty = (changedType: ChangeType) => store.commit(MAKE_DIRTY, changedType);
const resetChangeTracker = () => store.commit(RESET_CHANGE_TRACKER);
const setIsSaveFlowInProgress = (inProgress: boolean) => store.commit(SET_IS_SAVE_FLOW_IN_PROGRESS, inProgress);

let unsubscribeMutations = ref<(() => void) | null>(null);

const flowLoaded = ref(false);
const showDeleteModal = ref(false);
const showLeaveModal = ref(false);
const showPauseFlowModal = ref(false);
const showMakeLiveModal = ref(false);
const showBotPreviewModal = ref(false);
const showUpgradeModal = ref(false);
const showNoTriggerModal = ref(false);
const leaveModal = ref<typeof ConfirmationModal | null>(null);
let navigationNext: ((value?: boolean) => void) | null = null; // Store the next callback

const isSaveButtonEnabled = computed(() => !isSaveFlowInProgress.value && hasMadeChanges.value);
const isPreviewButtonEnabled = computed(
  () => !isFlowInvalid.value && !hasMadeChanges.value && !isSaveFlowInProgress.value,
);
const canPauseFlow = computed(
  () =>
    currentFlowStatus.value === FlowStatus.Live &&
    !hasLiveParentFlows() &&
    !hasLiveParentAnswers() &&
    !hasMadeChanges.value,
);
const canDeleteFlow = computed(() => parentFlows.value.length === 0 && parentAnswers.value.length === 0);
const canMakeLive = computed(
  () =>
    !isFlowInvalid.value &&
    !hasMadeChanges.value &&
    !isSaveFlowInProgress.value &&
    !isTransitioningToAnInactiveFlow.value &&
    isBotStudioEnabled(),
);
const previewFlowErrorMessage = computed(() =>
  hasMadeChanges.value ? 'Cannot preview a flow with unsaved changes' : '',
);
const saveFlowErrorMessage = computed(() => (!hasMadeChanges.value ? 'No changes to save' : ''));
const makeLiveErrorMessage = computed(() => {
  if (isTransitioningToAnInactiveFlow.value) {
    return 'Must not transition to flow or FAQ that is not live';
  }
  if (hasMadeChanges.value) {
    return 'Cannot make flow live with unsaved changes';
  }

  return '';
});

watch(
  () => props.flowId,
  async () => {
    await loadFlows(); // if there's no flows, (refresh has happened)
    await loadIntents(); // Required to display names
    flowLoaded.value = false;
    resetTracking();
    await fetchFlow();
    flowLoaded.value = true;
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (unsubscribeMutations.value) {
    unsubscribeMutations.value();
  }
  navigationNext = null;
});

onBeforeRouteUpdate((to, from, next) => {
  // called when the route that renders this component has changed,
  // but this component is reused in the new route.
  // For example, for a route with dynamic params `/foo/:id`, when we
  // navigate between `/foo/1` and `/foo/2`, the same `Foo` component instance
  // will be reused, and this hook will be called when that happens.
  // has access to `this` component instance.

  if (hasMadeChanges.value) {
    setupLeaveModal(next);
  } else {
    next();
  }
});

onBeforeRouteLeave((to, from, next) => {
  if (hasMadeChanges.value) {
    setupLeaveModal(next);
  } else {
    next();
  }
});

async function deleteFlow() {
  appInsights.trackEvent(
    { name: 'Delete flow clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );

  const flowToDeleteName = flowName.value;

  try {
    await flowsApi.deleteFlow(currentWorkspace.value.id, props.flowId);
    Toast.showSuccess(`Deleted flow ${flowToDeleteName}`);
    await resetTracking();
    await router.push({ name: 'bots-flows' });
  } catch {
    Toast.showError(`Failed to delete flow ${flowToDeleteName}`);
  }
}

async function pauseFlow() {
  try {
    setIsSaveFlowInProgress(true);
    await updateFlowStatus(FlowStatus.Paused);
    appInsights.trackEvent({ name: 'Flow paused' }, { flowId: props.flowId, workspaceId: currentWorkspace.value.id });
    Toast.showSuccess(`Paused ${flowName.value}`);
  } catch (error) {
    Toast.showError(`Failed to pause flow ${flowName.value}`);
  }
  setIsSaveFlowInProgress(false);
  showPauseFlowModal.value = false;
}

async function makeFlowLive() {
  try {
    setIsSaveFlowInProgress(true);
    await updateFlowStatus(FlowStatus.Live);
    appInsights.trackEvent(
      { name: 'Made flow live' },
      { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
    );
    Toast.showSuccess(`Made flow ${flowName.value} live`);
  } catch (error) {
    Toast.showError(`Failed to make flow ${flowName.value} live`);
  }
  setIsSaveFlowInProgress(false);
  showMakeLiveModal.value = false;
}

function saveButtonClicked() {
  appInsights.trackEvent(
    { name: 'Save flow clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );
  if (!isCurrentFlowTriggerable.value) {
    showNoTriggerModal.value = true;
  } else {
    saveChanges();
  }
}

function pauseFlowClicked() {
  appInsights.trackEvent(
    { name: 'Pause flow clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );
  if (!canPauseFlow.value) {
    Toast.showError(pauseFlowErrorMessage());
    return;
  }
  showPauseFlowModal.value = true;
}

function previewButtonClicked() {
  appInsights.trackEvent({ name: 'Preview clicked' }, { flowId: props.flowId, workspaceId: currentWorkspace.value.id });
  showBotPreviewModal.value = true;
}

function makeLiveButtonClicked() {
  appInsights.trackEvent(
    { name: 'Make live clicked' },
    { flowId: props.flowId, workspaceId: currentWorkspace.value.id },
  );
  showMakeLiveModal.value = true;
}

async function saveChanges() {
  showNoTriggerModal.value = false;
  setIsSaveFlowInProgress(true);

  setTimeout(async () => {
    try {
      await saveFlow();
      resetTracking();
      Toast.showSuccess('Successfully saved Flow');
    } catch (err: any) {
      Toast.showError(err.message);
    } finally {
      setIsSaveFlowInProgress(false);
    }
  }, 200);
}

function getDeleteConfirmationMessage() {
  return `Are you sure you want to delete <b>${HtmlFormatter.escapeHtmlCharacters(flowName.value)}</b>?`;
}

function getLeaveConfirmationMessage() {
  return `All changes you've made to <b>${HtmlFormatter.escapeHtmlCharacters(flowName.value)}</b> will be discarded. <br/>Are you sure you want to leave the page?`;
}

function getPauseConfirmationMessage() {
  return `This will prevent the flow from being triggered. Are you sure you want to continue?`;
}

function getMakeLiveConfirmationMessage() {
  return `This will make the flow active to your audience on the applicable channels. Are you sure you want to continue?`;
}

function getNoTriggerSelectedConfirmationMessage() {
  return `This flow has no triggers defined. Are you sure you want to continue?`;
}

function deleteFlowClicked() {
  if (!canDeleteFlow.value) {
    Toast.showError('Cannot delete a flow that is transitioned to by another flow or FAQ');
  } else {
    showDeleteModal.value = true;
  }
}

function isBotStudioEnabled() {
  return isWorkspaceFeatureEnabled(BOT_STUDIO_ENABLED);
}

function isPauseOptionVisible() {
  return currentFlowStatus.value === FlowStatus.Live;
}

function isMakeLiveButtonVisible() {
  return currentFlowStatus.value === FlowStatus.Paused || currentFlowStatus.value === FlowStatus.Draft;
}

function upgradeButtonClicked() {
  showUpgradeModal.value = true;
}

function hasLiveParentFlows() {
  return parentFlows.value.filter((parentFlow) => parentFlow.status === FlowStatus.Live).length > 0;
}

function hasLiveParentAnswers() {
  return parentAnswers.value.filter((parentAnswer) => parentAnswer.status === FlowStatus.Live).length > 0;
}

async function fetchFlow() {
  await loadFlow(props.flowId);
  flowLoaded.value = true;
}

function resetTracking() {
  resetChangeTracker();

  unsubscribeMutations.value = store.subscribe((mutation, state) => {
    trackChanges(mutation, state);
  });
}

function trackChanges(mutation: MutationPayload, state: any) {
  const changeType = determineChangeType(mutation.type);
  if (changeType) {
    makeDirty(changeType);
    return true;
  }
  return false;
}

function determineChangeType(type: string): ChangeType | null {
  switch (type) {
    case ADD_PATH:
    case REMOVE_PATH:
    case UPDATE_PATH:
    case ADD_STATE:
    case REMOVE_STATE:
    case UPDATE_STATE:
      return ChangeType.Paths;

    case SET_FLOW_NAME:
      return ChangeType.Name;

    case SET_TRIGGER:
      return ChangeType.Triggers;

    case UPDATE_SCHEDULE_CONDITION:
    case UPDATE_TRIGGER_CONDITIONS:
    case UPDATE_AUDIENCE_CONDITIONS:
    case UPDATE_TARGET_CONTACT_TYPES:
      return ChangeType.Conditions;

    default:
      return null;
  }
}

function setupLeaveModal(next: (value?: boolean) => void) {
  showLeaveModal.value = true;
  navigationNext = next;

  nextTick(() => {
    if (!leaveModal.value) return;
  });
}

function handleConfirmLeave() {
  showLeaveModal.value = false;
  if (navigationNext) {
    navigationNext(true); // Proceed with navigation
    navigationNext = null; // Clear after use
  }
}

function handleCancelLeave() {
  showLeaveModal.value = false;
  if (navigationNext) {
    navigationNext(false); // Cancel navigation
    navigationNext = null; // Clear after use
  }
}

function pauseFlowErrorMessage() {
  if (hasLiveParentFlows() || hasLiveParentAnswers()) {
    return 'Cannot pause a flow that is transitioned to by a live flow or FAQ';
  }
  if (hasMadeChanges.value) {
    return 'Cannot pause a flow with unsaved changes';
  }
  return '';
}
</script>

<style scoped lang="scss">
.section {
  padding: 0;

  &.content-container {
    display: flex;
  }
}

.title {
  font-weight: normal;
}

.back-arrow {
  display: flex;
  justify-content: center;
  width: 40px;
  height: 40px;
  cursor: pointer;

  background-color: $color-netcall-deep-sea;
  border-radius: 50%;

  color: $color-netcall-jonquil;

  &:hover {
    color: $color-netcall-deep-sea;
    background-color: $color-netcall-jonquil;
  }

  .icon-1-5x {
    bottom: 2px;
  }
}

.flow-title {
  margin-left: 0.5em;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 10px;

  .flow-status {
    align-items: center;
  }
}

.primary-header .level-item :deep(.dropdown-menu) {
  top: 120%;
}

.dropdown-item {
  .material-design-icon {
    margin-right: 0.5rem;
  }
}

.button-content {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  .material-design-icon {
    margin-right: 0.5rem;

    svg {
      bottom: 0;
    }
  }
}
</style>
